import { imageUrlConfig } from "./imageUrlConfig";
//=== 花瓣图片域名规则说明 ====
// 当前图片域名分成两部分，一部分域名前缀: bucket，一部分域名后缀: domain，
// 例如 gd-hbimg.huaban.com -> gd-hbimg(bucket)、huaban.com(domain)
//  *
//  * 当前的bucket和domain有以下几种
//  * bucket: gd-hbimg、hbimg、gd-hbimg-edge
//  * domain: huaban.com、huabanimg.com

// ========== 工具函数：尺寸计算 ==========
const SIZE_MAP = {
  fw: [86, 192, 240, 320, 480, 658, 1200],
  sq: [75, 180, 235, 240, 490], // squre 方图
};

// 广告后台或其他后台配置的图片，当file.bucket为OtherImageBucket时, 不能替换成其他域名访问
const OtherImageBucket = "gd-hbimg-other";

/**
 * 计算图片尺寸和 affix
 * @param {number} width - 目标宽度
 * @param {string} rect - 矩形类型 'fw' | 'sq'
 * @returns {{size: number, affix: string}}
 */
function calculateSize(width, rect) {
  const sizes = SIZE_MAP[rect];
  if (!sizes) {
    throw Error(`Unsupport rect value: ${rect}`);
  }

  let size = 1200; // 默认最大尺寸
  for (let value of sizes) {
    if (value >= width) {
      size = value;
      break;
    }
  }

  return {
    size,
    affix: `_${rect}`,
  };
}

// ========== 工具函数：分离 URL 和查询参数 ==========
/**
 * 分离 URL 和查询参数
 * @param {string} url - 完整 URL
 * @returns {{base: string, query: string}}
 */
function splitUrlAndQuery(url) {
  const [base, query] = url.split("?");
  return { base, query: query || "" };
}

// ========== 工具函数：从 URL 中提取 bucket ==========
/**
 * 从图片 URL 中提取 bucket
 * 当前图片域名分成两部分，一部分域名前缀: bucket，一部分域名后缀: domain，
 * 例如 gd-hbimg.huaban.com -> gd-hbimg(bucket)、huaban.com(domain)
 *
 * 当前的bucket和domain有以下几种
 * bucket: gd-hbimg、hbimg、gd-hbimg-edge
 * domain: huaban.com、huabanimg.com
 *
 * @param {string} imgSrc - 图片 URL
 * @returns {string} - 提取的 bucket，如果提取失败返回空字符串
 *
 * @example
 * // https://gd-hbimg.huabanimg.com/xxx -> gd-hbimg
 * // https://gd-hbimg-edge.huaban.com/xxx -> gd-hbimg-edge
 * // https://hbimg.huaban.com/xxx -> hbimg
 */
export function extractBucketFromUrl(imgSrc) {
  if (!imgSrc) {
    return "";
  }

  try {
    const url = new URL(imgSrc);
    const hostnameParts = url.hostname.split(".");
    // hostname格式: {bucket}.{domain}，例如 gd-hbimg.huabanimg.com
    // domain部分通常是最后两个部分（如 huabanimg.com），所以bucket是去掉最后两个部分后的内容
    if (hostnameParts.length >= 3) {
      return hostnameParts.slice(0, -2).join(".");
    } else if (hostnameParts.length === 2) {
      // 如果只有两部分，说明没有bucket前缀，使用第一部分作为bucket
      return hostnameParts[0];
    }
  } catch (e) {
    // 如果不是有效的URL，回退到原来的逻辑
    const parts = imgSrc.split(".");
    if (parts.length >= 3) {
      return parts.slice(0, -2).join(".");
    }
  }

  return "";
}

// ========== 工具函数：替换 URL 域名 ==========
/**
 * 根据配置替换 URL 中的域名
 * @param {string} url - 原始 URL
 * @param {object} file - 文件对象
 * @param {string} domain - 自定义域名（可选）
 * @returns {string} - 替换后的 URL
 *
 * @example
 * // 用例1: 配置域名与当前域名不同，自动替换
 * // 输入: url = "https://gd-hbimg-edge.huaban.com/small/image.jpg"
 * //      file = { bucket: "hbimg" }
 * //      配置: domain = "huabanimg.com"
 * // 返回: "https://gd-hbimg-edge.huabanimg.com/small/image.jpg"
 *
 * @example
 * // 用例2: 传入自定义 domain 参数，优先使用
 * // 输入: url = "https://gd-hbimg.huaban.com/image.jpg"
 * //      file = { bucket: "hbimg" }
 * //      domain = "huabanimg.com"
 * //      配置: domain = "huaban.com"
 * // 返回: "https://gd-hbimg.huabanimg.com/image.jpg" (使用传入的 domain)
 *
 * @example
 * // 用例3: 域名相同，不替换
 * // 输入: url = "https://gd-hbimg.huaban.com/image.jpg"
 * //      file = { bucket: "hbimg" }
 * //      配置: domain = "huaban.com"
 * // 返回: "https://gd-hbimg.huaban.com/image.jpg" (保持不变)
 *
 * @example
 * // 用例4: 没有 file.bucket 也没有 domain，不替换
 * // 输入: url = "https://example.com/image.jpg"
 * //      file = {}
 * // 返回: "https://example.com/image.jpg" (保持不变)
 *
 * @example
 * // 用例5: 完整 URL 示例（带查询参数）
 * // 输入: url = "https://gd-hbimg-edge.huaban.com/small/xxx_fw240webp?auth_key=xxx"
 * //      file = { bucket: "hbimg" }
 * //      配置: domain = "huabanimg.com"
 * // 返回: "https://gd-hbimg-edge.huabanimg.com/small/xxx_fw240webp?auth_key=xxx"
 *
 * @example
 * // 用例6: 实际 URL 示例
 * // 输入: url = "https://gd-hbimg-edge.huaban.com/small/dfe1574675fc36863a4751a4df7c0bae80f6c18112be41-GgGLbQ_fw240webp?auth_key=1764302400-ca85fa5d8a3849e48e609c9bd7b3c7fa-0-77be41b628bf79ed0eef470e2966e3dc"
 * //      file = { bucket: "hbimg" }
 * //
 * // 场景A: 配置 domain = "huaban.com" (与当前域名相同)
 * // 返回: 原 URL 不变（域名相同，不替换）
 * // "https://gd-hbimg-edge.huaban.com/small/dfe1574675fc36863a4751a4df7c0bae80f6c18112be41-GgGLbQ_fw240webp?auth_key=1764302400-ca85fa5d8a3849e48e609c9bd7b3c7fa-0-77be41b628bf79ed0eef470e2966e3dc"
 * //
 * // 场景B: 配置 domain = "huabanimg.com" (与当前域名不同)
 * // 返回: 替换域名部分，保持 bucket 前缀和查询参数
 * // "https://gd-hbimg-edge.huabanimg.com/small/dfe1574675fc36863a4751a4df7c0bae80f6c18112be41-GgGLbQ_fw240webp?auth_key=1764302400-ca85fa5d8a3849e48e609c9bd7b3c7fa-0-77be41b628bf79ed0eef470e2966e3dc"
 * //
 * // 场景C: 传入 domain = "huabanimg.com" (优先使用传入参数)
 * // 返回: 即使配置是 "huaban.com"，也使用传入的 "huabanimg.com"
 * // "https://gd-hbimg-edge.huabanimg.com/small/dfe1574675fc36863a4751a4df7c0bae80f6c18112be41-GgGLbQ_fw240webp?auth_key=1764302400-ca85fa5d8a3849e48e609c9bd7b3c7fa-0-77be41b628bf79ed0eef470e2966e3dc"
 */
function replaceUrlDomain(url, file, domain, bucket) {
  try {
    const urlObj = new URL(url);

    // 当前指定使用的Domain与Bucket
    let currentDomain = domain;
    let currentBucket = bucket;

    if (!currentDomain && file.bucket) {
      const config = imageUrlConfig.get(file.bucket);
      /** 是否需要切换域名 */
      const isSwitchFlag = imageUrlConfig.hasSwitchFlag;

      // 有记录切换过域名时，才进行域名切换
      if (isSwitchFlag) {
        currentDomain = config.domain;
        currentBucket = config.bucket;
      }
    }

    // 如果当前没有指定使用的，不进行替换
    if (!currentDomain && !currentBucket) {
      return url;
    }

    // 广告后台或其他后台配置的图片，当file.bucket为OtherImageBucket时, 不能替换成其他域名访问
    if (file.bucket === OtherImageBucket) {
      currentBucket = file.bucket;
    }

    // 提取当前 URL 的域名（取最后两部分，如 gd-hbimg.huaban.com -> huaban.com）
    const hostnameParts = urlObj.hostname.split(".");
    if (hostnameParts.length >= 2) {
      const urlDomain = hostnameParts.slice(-2).join(".");
      const urlBucket = hostnameParts[0];

      // 如果存在不一致，需要替换指定使用的
      if (urlDomain !== currentDomain || urlBucket !== currentBucket) {
        urlObj.hostname = `${currentBucket || urlBucket}.${
          currentDomain || urlDomain
        }`;

        return urlObj.toString();
      }
    }
  } catch (e) {
    // 如果不是合法URL，不处理域名替换
    console.error("replaceUrlDomain error", e);
  }
  return url;
}

// ========== 工具函数：应用格式后缀 ==========
/**
 * 应用格式后缀到 URL
 * @param {string} baseUrl - 基础 URL（不含查询参数）
 * @param {object} file - 文件对象
 * @param {object} options - 选项
 * @param {number} options.size - 图片尺寸
 * @param {boolean} options.origin - 是否原图
 * @param {boolean} options.gif - 是否 gif
 * @param {boolean} options.webp - 是否 webp
 * @returns {string} - 添加格式后缀后的 URL
 */
function applyFormatSuffix(baseUrl, file, { size, origin, gif, webp }) {
  const fileType = typeof file === "string" ? null : file.type;
  const isHeic = fileType === "image/heic";
  let url = baseUrl;

  // heic 格式处理
  if (isHeic) {
    if (size === 658 || size === 1200) {
      url += origin ? "_png" : "png";
    } else if (origin) {
      url += "_png";
    }
  }

  // gif 格式处理
  if (gif && (size === 240 || size === 320 || size === 236 || size === 480)) {
    url += "g";
  }

  // webp 格式处理
  if (
    webp &&
    !origin &&
    !isHeic &&
    !gif &&
    isSupportWebp() &&
    [86, 192, 240, 480, 75, 180, 490, 658, 1200].includes(size)
  ) {
    let canUseWebp = false;
    if (typeof file === "string") {
      canUseWebp = true;
    } else {
      // webp图片格式支持最大图片单边尺寸限制 16383px
      canUseWebp =
        file.height &&
        file.width &&
        (file.height < 16380 || (file.height * size) / file.width < 16380) &&
        fileType !== "image/gif";
    }

    if (canUseWebp) {
      url += "webp";
    }
  } else if (
    ["image/jpeg", "image/jpg"].includes(fileType) &&
    [75, 86, 180, 192, 240, 480, 490].includes(size)
  ) {
    // 原图片格式是jpeg且指定尺寸的图片使用jpeg格式
    url += "jpeg";
  }

  return url;
}

// ========== 工具函数：构建旧版 URL ==========
/**
 * 构建旧版 URL（使用 bucket + key）
 * @param {object} file - 文件对象
 * @param {object} options - 选项
 * @param {number} options.size - 图片尺寸
 * @param {string} options.affix - 尺寸 affix
 * @param {boolean} options.origin - 是否原图
 * @param {string} options.domain - 自定义域名
 * @param {string} options.bucket - 自定义 bucket
 * @returns {{url: string, queryString: string}} - URL 和查询参数
 */
function buildLegacyUrl(file, { size, affix, origin, domain, bucket }) {
  let path = "";
  let url = "";
  let queryString = "";

  if (typeof file === "string") {
    if (origin || /_(?:fw|sq|png|jpeg|webp)/.test(file)) {
      // 原图或者已经处理过，直接返回
      return { url: file, queryString: "" };
    }
    // 处理带查询参数的字符串 URL
    if (file.includes("?")) {
      const { base, query } = splitUrlAndQuery(file);
      url = `${base}${affix}${size}`;
      queryString = query;
    } else {
      url = file + affix + size;
    }
  } else {
    // TODO: 目前配置域名写在用户浏览器本地，会导致SSR和本地渲染不一致报错（待处理） @城管
    const imageConfig = imageUrlConfig.get(file.bucket);
    const realDomain = domain || imageConfig.domain;

    let realBucket =
      bucket || imageUrlConfig.hasSwitchFlag ? imageConfig.bucket : file.bucket;

    if (file.bucket === OtherImageBucket) {
      realBucket = file.bucket;
    }

    if (realBucket && ["gd-hbimg", "hbimg"].includes(realBucket)) {
      realBucket = "gd-hbimg-edge";
    }

    // 小图场景下，使用small作为图片展示，主要用于素材无水印小图展示
    // 有alias的图片，使用alias路径作为图片展示
    path = file.alias ? "alias/" : size <= 480 ? file.small && "small/" : "";

    url = `https://${realBucket}.${realDomain}/${path || ""}${file.key}${
      origin ? "" : affix + size
    }`;
  }

  // 如果 url 中还有查询参数（字符串类型 URL 已分离，对象类型可能没有）
  if (!queryString && url.includes("?")) {
    const { base, query } = splitUrlAndQuery(url);
    url = base;
    queryString = query;
  }

  return { url, queryString };
}

// ========== 主函数 ==========
/**
 * @param file
 * @param {*} props
 * @returns {string|undefined}
 */
export default function imageUrl(
  file,
  {
    width,
    rect = "fw",
    origin = false,
    gif = false,
    webp = true,
    domain,
    bucket,
  } = {}
) {
  if (!file || ((!file.bucket || !file.key) && typeof file !== "string")) {
    return null;
  }

  // 计算尺寸和 affix
  const { size, affix } = calculateSize(width, rect);

  // ========== 优先使用新版 url 字段 ==========
  if (file.url && typeof file !== "string") {
    // file.url 可能为完整的 url，需要将 path 插入到 url 的第一层级
    // 比如 file.url = 'https://foo.com/bar.jpg'，file.path = 'abc'
    // 需要得到 'https://foo.com/abc/bar.jpg'

    const path = file.alias
      ? "alias"
      : size <= 480
      ? file.small && "small"
      : "";

    let baseUrl = file.url;
    let queryString = "";

    // 分离 URL 和查询参数
    if (baseUrl.includes("?")) {
      const split = splitUrlAndQuery(baseUrl);
      baseUrl = split.base;
      queryString = split.query;
    }

    // 支持兜底 domain 逻辑：对比并替换域名
    baseUrl = replaceUrlDomain(baseUrl, file, domain, bucket);

    // 处理 path 插入
    if (path) {
      try {
        const urlObj = new URL(baseUrl);
        // urlObj.pathname 形如 "/bar.jpg"
        const segments = urlObj.pathname.split("/").filter(Boolean);
        segments.unshift(path);
        urlObj.pathname = "/" + segments.join("/");
        baseUrl = urlObj.toString();
      } catch (e) {
        // 如果不是合法URL，不处理
      }
    }

    // 如果是原图，直接返回
    if (origin) {
      return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    // 检查是否已经包含尺寸参数，避免重复添加
    if (/_(?:fw|sq|png|jpeg|webp)/.test(baseUrl)) {
      return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    // 应用尺寸参数
    baseUrl = baseUrl + affix + size;

    // 应用格式后缀
    const finalUrl = applyFormatSuffix(baseUrl, file, {
      size,
      origin,
      gif,
      webp,
    });

    // 重新组合 URL 和查询参数
    return queryString ? `${finalUrl}?${queryString}` : finalUrl;
  }

  // ========== 降级：使用旧版逻辑 ==========
  // 构建旧版 URL
  const { url: baseUrl, queryString } = buildLegacyUrl(file, {
    size,
    affix,
    origin,
    domain,
    bucket,
  });

  // 如果是原图或已处理的字符串 URL，直接返回
  if (
    typeof file === "string" &&
    (origin || /_(?:fw|sq|png|jpeg|webp)/.test(file))
  ) {
    return baseUrl;
  }

  // 应用格式后缀
  const finalUrl = applyFormatSuffix(baseUrl, file, {
    size,
    origin,
    gif,
    webp,
  });

  // 重新组合 URL 和查询参数
  return queryString ? `${finalUrl}?${queryString}` : finalUrl;
}

export function getErrorPageImage(file, { width } = {}) {
  if (!file || !file.bucket || !file.key) {
    return null;
  }
  let affix = "";
  if (width === 82.5) {
    // 详情页画板小图
    affix = `_fw86`;
  } else if (width) {
    affix = `_fw${width}`;
  } else {
    affix = `_fw240`;
  }

  if (file.bucket.indexOf("gd-") !== 0) {
    file.bucket = "gd-" + file.bucket;
  }

  return `https://${file.bucket}.huaban.com/img/error_page/${file.key}${affix}`;
}

export function getAuthorizePageImage(file, { width } = {}) {
  if (!file || !file.bucket || !file.key) {
    return null;
  }
  let affix = "";
  if (width === 28) {
    // 详情页画板小图
    affix = `_fw86`;
  } else if (width) {
    affix = `_fw${width}`;
  } else {
    affix = `_fw240`;
  }

  if (file.bucket.indexOf("gd-") !== 0) {
    file.bucket = "gd-" + file.bucket;
  }

  return `https://${file.bucket}.huaban.com/img/apps/${file.key}${affix}`;
}

/**
 * 获取 oss 图片地址
 * @param {string} bucket
 * @param {string} key
 * @param {Record} config
 */
export function getOSSImage(bucket, key, config) {
  if (bucket.indexOf("gd-") !== 0) {
    bucket = "gd-" + bucket;
  }

  let configStr = "";
  for (const [func, param] of Object.entries(config ?? {})) {
    if (
      param === undefined ||
      param === null ||
      (typeof param === "number" && isNaN(param))
    ) {
      continue;
    }
    configStr += `/${func}/${param}`;
  }
  return `https://${bucket}.huaban.com/${key}_${configStr}`;
}

/**
 * 获取webp格式图片
 * @param {string} bucket
 * @param {string} key
 * @param {Record} config
 */
export function getWebp(bucket, key, config) {
  return getOSSImage(bucket, key, { ...config, format: "webp" });
}

/**
 * 获取指定尺寸的替身图片，按照最小边缩放并居中裁剪
 * @param {string} bucket
 * @param {string} key
 * @param {number} width
 * @param {number=} height 默认和width一致
 */
export function getAvatar(bucket, key, width, height) {
  return getWebp(bucket, key, {
    both: `${width}x${height ?? width}`,
  });
}

/**
 * 检测浏览器是否支持webp格式的图片
 */
export function isSupportWebp() {
  if (isSupportWebp.supportValue === undefined) {
    let result = false;

    try {
      const elem = document.createElement("canvas");
      if (elem.getContext && elem.getContext("2d")) {
        // was able or not to get WebP representation
        result = elem.toDataURL("image/webp").indexOf("data:image/webp") === 0;
      } else {
        // very old browser like IE 8, canvas not supported
        result = false;
      }
    } catch (e) {
      console.error("isSupportWebp error", e);
      result = false;
    }

    isSupportWebp.supportValue = result;
  }
  return isSupportWebp.supportValue;
}

export function gdOssUrl(url, options = {}) {
  // 其他域名不处理
  if (
    !/dancf\.com/.test(url) &&
    !/grocery-cdn/.test(url) &&
    !/gd-hb/.test(url)
  ) {
    return url;
  }

  // 避免重复处理
  if (/\?x-oss-process=/.test(url)) return url;
  const {
    width,
    height,
    type = "webp",
    scaleType,
    minDpr = 1.5,
    useDpr = true,
    resizeType = 0,
    sharpen = 110,
    crop,
  } = options;
  let src = url + (url.includes("?") ? "&" : "?") + "x-oss-process=";
  if ((width || height) && !/\.gif$/.test(url)) {
    src += "image/resize";

    // 最大限制 2， 防止图片过大， 最小1.5 保证图片清晰
    const dpr = useDpr ? Math.min(2, minDpr) : 1;

    if (width) {
      src += `,w_${Math.round(width * dpr)}`;
    }
    if (height) {
      src += `,h_${Math.round(height * dpr)}`;
    }
    if (scaleType) {
      // https://help.aliyun.com/document_detail/44688.html?spm=a2c4g.11186623.6.1383.36e335a8pxsRMg
      src += `,m_${scaleType}`;
    }
    if (resizeType === 6) {
      src += ",type_6";
    }
    if (crop) {
      src += "/crop";
      if (crop.width) {
        src += `,w_${Math.round(crop.width)}`;
      }
      if (crop.height) {
        src += `,h_${Math.round(crop.height)}`;
      }
    }
    if (sharpen) {
      src += `/sharpen,${sharpen}`;
    }
    src += "/interlace,1,";
  }

  if (type && !/\.svg$/.test(url)) {
    if (type === "webp" || /\.webp$/.test(url)) {
      if (isSupportWebp()) {
        src += `image/format,${type},`;
      } else if (options.allowJPGOnUnsupportedWebp && !/\.gif$/.test(url)) {
        src += "image/format,jpg,";
      }
    } else {
      src += `image/format,${type},`;
    }
  }
  if (/\?x-oss-process=$/.test(src)) return url;
  return src.replace(/,$/, "");
}
/**
 * 图片展示src优化策略：
 * @param url  预览图片地址
 * @param {Partial< {
 *  displayWidth: number,
 *  displayHeight: number,
 *  width: number,
 *  height: number,
 *  aspectRatio: number,
 *  sharpen: number,
 *  type: string
 * }>} options 其他配置
 {
     displayWidth: 图片展示宽度
     Width: 图片的实际宽度尺寸
     height：预览图片的高度尺寸
     aspectRatio: 要显示的高宽比
 }
 */
export function gdImgSrcPolicy(url, options = {}) {
  if (url?.includes(".svg")) {
    return {
      src: url,
    };
  }

  const {
    displayWidth,
    width = 1600,
    height,
    aspectRatio,
    sharpen = 80,
    ...rest
  } = options;

  const ossOptions = {
    minDpr: 1,
    sharpen,
    ...rest,
  };

  // 按高度展示
  if (options.displayHeight) {
    // 检查处理后宽度是否会存在没有容器宽的情况
    if (width && height && aspectRatio) {
      const displayHeight = options.displayHeight;
      let previewAspectRatio = height / width;
      // 转换后展示的宽度
      let tranformWidth = displayHeight / previewAspectRatio;
      // 实际容器的宽度
      let displayWidth = displayHeight / aspectRatio;

      // 宽度不够，用实际容器宽度来做oss处理
      if (displayWidth > tranformWidth) {
        return {
          src: gdOssUrl(url, {
            width: displayWidth,
            ...ossOptions,
          }),
          srcSet:
            gdOssUrl(url, {
              width: displayWidth,
              ...ossOptions,
              minDpr: 2,
            }) + " 2x",
        };
      }
    }

    return {
      src: gdOssUrl(url, {
        height: options.displayHeight,
        minDpr: 1,
        ...ossOptions,
      }),
      srcSet:
        gdOssUrl(url, {
          height: options.displayHeight,
          ...ossOptions,
          minDpr: 2,
        }) + " 2x",
    };
  }

  let src = gdOssUrl(url, {
    width: displayWidth,
    ...ossOptions,
  });

  let srcset =
    gdOssUrl(url, {
      width: displayWidth,
      ...ossOptions,
      minDpr: 2,
    }) + " 2x";

  // 检查高度是否会存在没有容器高的情况
  if (width && height && aspectRatio) {
    let previewAspectRatio = height / width;
    // 转换后展示的高度
    let tranformHeight = displayWidth * previewAspectRatio;
    // 实际容器的高度
    let displayHeight = displayWidth * aspectRatio;

    // 如果转换后的高度填不满要显示的高度，则用实际容器展示高度来做oss处理
    if (displayHeight > tranformHeight) {
      src = gdOssUrl(url, {
        height: displayHeight,
        ...ossOptions,
      });

      srcset =
        gdOssUrl(url, {
          height: displayHeight,
          ...ossOptions,
          minDpr: 2,
        }) + " 2x";
    }
  }

  return {
    src,
    srcSet: srcset,
  };
}

// 最好在客户端渲染使用，服务端只返回1倍屏
// 获取合适分辨率倍数的图片
/**
 *
 * @param {string} url
 * @param {{
 *  width?: number,
 *  height?: number,
 *  sharpen?: number,
 *  type?: string
 * }} option
 * @returns
 */
export function getAdaptiveImg(url, option) {
  if (typeof window === "undefined") {
    return gdOssUrl(url, {
      minDpr: 1,
      ...option,
    });
  }
  return gdOssUrl(url, {
    minDpr: window.devicePixelRatio,
    ...option,
  });
}

export function getImageSrc2x(...args) {
  const [file, options] = args;
  return {
    src: imageUrl(...args),
    srcSet:
      imageUrl(file, {
        ...options,
        ...(options && options.width ? { width: options.width * 2 } : null),
      }) + " 2x",
  };
}

/**
 * 在不确定图片类型的情况下，统一使用该方法处理图片地址
 * @param {*} filrOrUrl 图片地址或图片对象
 * @param {*} option 图片处理参数
 * @returns
 */
export function commonImgUrl(filrOrUrl, option) {
  if (typeof filrOrUrl === "string") {
    // 判断域名是否是花瓣采集图片的域名
    // gd-hbimg.huabanimg.com
    // hbimg.huabanimg.com
    // gd-hbimg.huaban.com  (当前主要域名)
    // hbimg.huaban.com
    // gd-hbimg-edge.huaban.com  (新增：鉴权域名)
    // gd-hbimg-edge.huabanimg.com (新增：鉴权域名)
    // gd-hbimg-edge-fat.huabanimg.com
    // gd-hbimg-edge-fat.huaban.com

    if (
      /gd-hbimg-edge\.huabanimg\.com|gd-hbimg\.huabanimg\.com|hbimg\.huabanimg\.com|gd-hbimg\.huaban\.com|hbimg\.huaban\.com|gd-hbimg-edge\.huaban\.com|gd-hbimg-edge-fat\.huabanimg\.com|gd-hbimg-edge-fat\.huaban\.com/.test(
        filrOrUrl
      )
    ) {
      return imageUrl(filrOrUrl, option);
    }

    // 兼容处理下gdOssUrl option参数差异
    return gdOssUrl(filrOrUrl, option);
  } else if (typeof filrOrUrl === "object") {
    return imageUrl(filrOrUrl, option);
  }

  // 非法参数返回原值
  console.warn("commonImgUrl: invalid parameter", filrOrUrl, option);
  return filrOrUrl;
}
